import ImageWIthSignedUrl from "components/S3Utility/ImageWIthSignedUrl";
import { setNotification } from "features/Notification/notificationSlice";
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import authHeader from "services/auth-header";
import API from "services/axios";
import { Modal } from "react-bootstrap";

export default function AddProductApprovalColors({ formData, setFormData }) {
  /*   All States
   ********************************************* */
  const [approvalColors, setApprovalColors] = useState([]);
  const [selectedAvailableIds, setSelectedAvailableIds] = useState([]);
  const [selectedColorObjects, setSelectedColorObjects] = useState([]); // Add this new state
  const [showAddModal, setShowAddModal] = useState(false);
  const [availableColors, setAvailableColors] = useState([]);
  const [availableSearchTerm, setAvailableSearchTerm] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [availablePage, setAvailablePage] = useState(1);
  const [availableTotalPages, setAvailableTotalPages] = useState(1);
  const [listLoading, setListLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(false);

  const dispatch = useDispatch();

  /*   All Functions
   ********************************************* */

  const fetchColorsByIds = async (colorIds) => {
    if (!colorIds || colorIds.length === 0) {
      setApprovalColors([]);
      return;
    }

    try {
      setInitialLoading(true);
      const { data } = await API.post(
        "/sales-wo-colors-by-ids/products",
        { colorIds },
        { headers: authHeader() },
      );
      if (data?.success) {
        const colors = data.data || [];
        setApprovalColors(colors);
      } else {
        dispatch(
          setNotification({
            message: data?.message || "Failed to load approval colors",
            type: "error",
          }),
        );
        setApprovalColors([]);
      }
    } catch (err) {
      dispatch(setNotification({ message: err.message, type: "error" }));
      setApprovalColors([]);
    } finally {
      setInitialLoading(false);
    }
  };

  const fetchAvailableColors = async ({ reset = false, query, page } = {}) => {
    try {
      setListLoading(true);
      const nextPage = reset ? 1 : (page ?? availablePage);
      const searchValue =
        typeof query === "string" ? query.trim() : appliedSearch;
      const { data } = await API.post(
        `/sales-wo-colors?page=${nextPage}&limit=10`,
        { query: searchValue || "" },
        { headers: authHeader() },
      );
      if (data?.success) {
        setAvailableColors((prev) =>
          reset ? data.data || [] : [...prev, ...(data.data || [])],
        );
        setAvailablePage(nextPage);
        setAvailableTotalPages(data?.totalPages || nextPage);
        if (typeof query === "string") {
          setAppliedSearch(searchValue);
        }
      } else {
        dispatch(
          setNotification({
            message: data?.message || "Failed to load colors",
            type: "error",
          }),
        );
      }
    } catch (err) {
      dispatch(setNotification({ message: err.message, type: "error" }));
    } finally {
      setListLoading(false);
    }
  };

  const openAddModal = () => {
    setAvailableColors([]);
    setAvailableSearchTerm("");
    setAppliedSearch("");
    setAvailablePage(1);
    setAvailableTotalPages(1);
    setSelectedAvailableIds([]);
    setShowAddModal(true);
    fetchAvailableColors({ reset: true, query: "" });
  };

  const closeAddModal = () => {
    setShowAddModal(false);
    setAvailableColors([]);
    setAvailableSearchTerm("");
    setAppliedSearch("");
    setAvailablePage(1);
    setAvailableTotalPages(1);
    setSelectedAvailableIds([]);
    setSelectedColorObjects([]); // Clear selected objects
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    fetchAvailableColors({ reset: true, query: availableSearchTerm });
  };

  const extractColorIds = (colors = []) =>
    colors
      .map((item) => item?.colorId)
      .filter(
        (colorId, index, arr) => colorId && arr.indexOf(colorId) === index,
      );

  const toggleAvailableSelection = (color) => {
    const colorId = color?.colorId;
    const currentIds = formData?.approvalColors || [];
    if (!colorId || currentIds.includes(colorId)) return;

    setSelectedAvailableIds((prev) => {
      if (prev.includes(colorId)) {
        // Remove from selected
        setSelectedColorObjects((objects) =>
          objects.filter((obj) => obj.colorId !== colorId),
        );
        return prev.filter((id) => id !== colorId);
      } else {
        // Add to selected
        setSelectedColorObjects((objects) => [...objects, color]);
        return [...prev, colorId];
      }
    });
  };

  const handleAddSelectedColors = () => {
    if (!selectedAvailableIds.length) return;

    const currentIds = formData?.approvalColors || [];

    // Use selectedColorObjects instead of filtering from availableColors
    const uniqueNewSelections = selectedColorObjects.filter(
      (color) =>
        color?.colorId &&
        selectedAvailableIds.includes(color.colorId) &&
        !currentIds.includes(color.colorId),
    );

    if (!uniqueNewSelections.length) {
      dispatch(
        setNotification({
          message: "Selected colors are already added.",
          type: "info",
        }),
      );
      return;
    }

    const newColorIds = extractColorIds(uniqueNewSelections);
    const nextColors = [...approvalColors, ...uniqueNewSelections];

    setFormData((prev) => ({
      ...prev,
      approvalColors: [...currentIds, ...newColorIds],
    }));

    setApprovalColors(nextColors);
    setSelectedAvailableIds([]);
    setSelectedColorObjects([]); // Clear selected objects
    closeAddModal();
  };

  const handleRemoveColor = (color) => {
    const colorId = color?.colorId;
    if (!colorId || !approvalColors.length) return;

    const nextColors = approvalColors.filter(
      (item) => item?.colorId !== colorId,
    );
    const currentIds = formData?.approvalColors || [];

    setFormData((prev) => ({
      ...prev,
      approvalColors: currentIds.filter((id) => id !== colorId),
    }));

    setApprovalColors(nextColors);
  };

  /*   All UseEffects
   ********************************************* */
  useEffect(() => {
    const colorIds = formData?.approvalColors || [];
    if (Array.isArray(colorIds) && colorIds.length > 0) {
      fetchColorsByIds(colorIds);
    } else {
      setApprovalColors([]);
    }
  }, [formData?.approvalColors]);

  return (
    <div className="h-100">
      {initialLoading && (
        <div
          className="position-absolute w-100 h-100 d-flex align-items-center justify-content-center rounded"
          style={{
            zIndex: 10,
            backgroundColor: "rgba(255, 255, 255, 0.95)",
            backdropFilter: "blur(4px)",
          }}
        >
          <div className="text-center">
            <div
              className="spinner-border spinner-border-sm text-primary mb-1"
              role="status"
            >
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="text-muted mb-0" style={{ fontSize: "0.75rem" }}>
              Loading colors...
            </p>
          </div>
        </div>
      )}

      <div className="card border shadow-sm h-100">
        <div className="card-header bg-white border-bottom py-2 px-3">
          <div className="d-flex align-items-center justify-content-between">
            <div className="d-flex align-items-center gap-2">
              <i
                className="mdi mdi-palette text-primary"
                style={{ fontSize: "1.1rem" }}
              />
              <span className="fw-semibold mb-0" style={{ fontSize: "0.9rem" }}>
                Approval Colors
              </span>
              <span
                className="badge bg-primary bg-opacity-10 text-primary rounded-pill px-2 py-1"
                style={{ fontSize: "0.7rem" }}
              >
                {approvalColors.length}
              </span>
            </div>
            <button
              className="btn btn-primary btn-sm d-flex align-items-center gap-1 px-2 py-1 shadow-sm"
              onClick={openAddModal}
              style={{ transition: "all 0.2s ease", fontSize: "0.8rem" }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.transform = "translateY(-1px)")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.transform = "translateY(0)")
              }
            >
              <i
                className="mdi mdi-plus-circle"
                style={{ fontSize: "0.9rem" }}
              />
              <span>Add</span>
            </button>
          </div>
        </div>

        <div className="card-body p-0">
          <div
            className="p-2"
            style={{
              height: 450,
              overflowY: "auto",
            }}
          >
            {approvalColors.length === 0 && !initialLoading && (
              <div className="text-center py-4">
                <i
                  className="mdi mdi-palette-outline text-muted"
                  style={{ fontSize: "2.5rem", opacity: 0.3 }}
                />
                <p
                  className="text-muted mt-2 mb-0"
                  style={{ fontSize: "0.85rem" }}
                >
                  No colors added yet
                </p>
                <small className="text-muted" style={{ fontSize: "0.75rem" }}>
                  Click "Add" to get started
                </small>
              </div>
            )}

            <div className="d-flex flex-column gap-1">
              {approvalColors.map((color) => (
                <div key={color.colorId || color.id}>
                  <div className="card mb-0 border shadow-sm position-relative">
                    <div className="card-body px-2 py-1">
                      <div className="d-flex align-items-center justify-content-between">
                        <div className="d-flex align-items-center gap-2 flex-grow-1 min-w-0">
                          <div
                            className="d-flex align-items-center justify-content-center bg-light rounded"
                            style={{
                              width: 28,
                              height: 28,
                              minWidth: 28,
                              overflow: "hidden",
                              border: "1px solid #e9ecef",
                            }}
                          >
                            <ImageWIthSignedUrl
                              path={color?.fileName}
                              imgStyle={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                              }}
                              alt={color.fileName}
                            />
                          </div>
                          <div className="flex-grow-1 min-w-0">
                            <div
                              className="fw-semibold text-truncate"
                              style={{ fontSize: "0.85rem" }}
                            >
                              {color.name}
                            </div>
                          </div>
                        </div>

                        <button
                          className="btn btn-sm btn-light text-danger border-0 rounded-circle d-flex align-items-center justify-content-center p-0"
                          onClick={() => handleRemoveColor(color)}
                          style={{
                            width: 26,
                            height: 26,
                            minWidth: 26,
                            transition: "all 0.15s ease",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = "#dc3545";
                            e.currentTarget.style.color = "white";
                            e.currentTarget.style.transform = "scale(1.1)";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = "";
                            e.currentTarget.style.color = "";
                            e.currentTarget.style.transform = "scale(1)";
                          }}
                          title="Remove color"
                        >
                          <i
                            className="mdi mdi-delete"
                            style={{ fontSize: "0.95rem" }}
                          />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {showAddModal && (
        <Modal
          show={showAddModal}
          onHide={closeAddModal}
          contentClassName="border-0 shadow-lg"
          size="lg"
        >
          <Modal.Header className="border-bottom bg-light py-2 px-3">
            <Modal.Title
              className="d-flex align-items-center gap-2"
              style={{ fontSize: "1rem" }}
            >
              <i
                className="mdi mdi-palette-outline text-primary"
                style={{ fontSize: "1.2rem" }}
              />
              <span>Select Colors</span>
            </Modal.Title>
            <button
              type="button"
              className="btn-close btn-close-sm"
              onClick={closeAddModal}
            />
          </Modal.Header>
          <Modal.Body className="p-3">
            <form className="mb-3" onSubmit={handleSearchSubmit}>
              <div className="input-group input-group-sm shadow-sm">
                <span className="input-group-text bg-white border-end-0 px-2">
                  <i
                    className="mdi mdi-magnify text-muted"
                    style={{ fontSize: "1rem" }}
                  />
                </span>
                <input
                  className="form-control border-start-0 ps-0"
                  placeholder="Search colors..."
                  value={availableSearchTerm}
                  onChange={(e) => setAvailableSearchTerm(e.target.value)}
                  disabled={listLoading}
                  style={{ fontSize: "0.85rem" }}
                />
                <button
                  className="btn btn-primary px-3"
                  type="submit"
                  disabled={listLoading}
                  style={{ fontSize: "0.85rem" }}
                >
                  <i className="mdi mdi-magnify" />
                </button>
              </div>
            </form>

            <div className="mb-2">
              <small className="text-muted" style={{ fontSize: "0.75rem" }}>
                <i className="mdi mdi-information-outline me-1" />
                {selectedAvailableIds.length > 0
                  ? `${selectedAvailableIds.length} color${
                      selectedAvailableIds.length > 1 ? "s" : ""
                    } selected`
                  : "Select colors to add"}
              </small>
            </div>

            <div
              className="border rounded"
              style={{
                maxHeight: 350,
                overflowY: "auto",
                backgroundColor: "#fafafa",
              }}
            >
              {availableColors.length === 0 && !listLoading && (
                <div className="text-center py-4">
                  <i
                    className="mdi mdi-palette-outline text-muted"
                    style={{ fontSize: "2rem", opacity: 0.3 }}
                  />
                  <p
                    className="text-muted mt-2 mb-0"
                    style={{ fontSize: "0.85rem" }}
                  >
                    No colors found
                  </p>
                  <small className="text-muted" style={{ fontSize: "0.75rem" }}>
                    Try adjusting your search
                  </small>
                </div>
              )}

              {availableColors.map((color) => {
                const isSelected = selectedAvailableIds.includes(color.colorId);
                const alreadyAdded = (formData?.approvalColors || []).includes(
                  color.colorId,
                );
                return (
                  <div
                    key={color.colorId}
                    className={`px-2 py-1 border-bottom position-relative ${
                      alreadyAdded
                        ? "bg-success bg-opacity-10"
                        : isSelected
                          ? "bg-primary"
                          : "bg-white"
                    }`}
                    style={{
                      cursor: alreadyAdded ? "not-allowed" : "pointer",
                      transition: "all 0.15s ease",
                      opacity: alreadyAdded ? 0.7 : 1,
                    }}
                    onClick={() =>
                      !alreadyAdded && toggleAvailableSelection(color)
                    }
                    onMouseEnter={(e) => {
                      if (!alreadyAdded && !isSelected) {
                        e.currentTarget.style.backgroundColor = "#f8f9fa";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!alreadyAdded && !isSelected) {
                        e.currentTarget.style.backgroundColor = "white";
                      }
                    }}
                  >
                    <div className="d-flex justify-content-between align-items-center">
                      <div className="d-flex align-items-center gap-2 flex-grow-1 min-w-0">
                        <div
                          className={`d-flex align-items-center justify-content-center rounded ${
                            isSelected && !alreadyAdded
                              ? "border-white border-2"
                              : "border"
                          }`}
                          style={{
                            width: 28,
                            height: 28,
                            minWidth: 28,
                            overflow: "hidden",
                            backgroundColor: "#e9ecef",
                          }}
                        >
                          {color?.fileName && (
                            <ImageWIthSignedUrl
                              path={color?.fileName}
                              imgStyle={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                              }}
                            />
                          )}
                        </div>
                        <div className="flex-grow-1 min-w-0">
                          <div
                            className={`fw-semibold text-truncate ${
                              isSelected && !alreadyAdded ? "text-white" : ""
                            }`}
                            style={{ fontSize: "0.85rem" }}
                          >
                            {color.name}
                          </div>
                        </div>
                      </div>

                      <div className="d-flex align-items-center ms-2">
                        {alreadyAdded ? (
                          <span
                            className="badge bg-success px-2 py-1"
                            style={{ fontSize: "0.7rem" }}
                          >
                            <i
                              className="mdi mdi-check-circle"
                              style={{ fontSize: "0.8rem" }}
                            />
                          </span>
                        ) : (
                          <div
                            className={`rounded-circle d-flex align-items-center justify-content-center ${
                              isSelected ? "bg-white" : "border border-2"
                            }`}
                            style={{
                              width: 22,
                              height: 22,
                              minWidth: 22,
                              transition: "all 0.15s ease",
                            }}
                          >
                            {isSelected ? (
                              <i
                                className="mdi mdi-check text-primary"
                                style={{ fontSize: "0.85rem" }}
                              />
                            ) : (
                              <div style={{ width: 8, height: 8 }} />
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}

              {listLoading && (
                <div className="text-center py-3 bg-white">
                  <div
                    className="spinner-border spinner-border-sm text-primary mb-1"
                    role="status"
                  >
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p
                    className="text-muted mb-0"
                    style={{ fontSize: "0.75rem" }}
                  >
                    Loading colors...
                  </p>
                </div>
              )}
            </div>

            {availablePage < availableTotalPages && !listLoading && (
              <div className="text-center mt-2">
                <button
                  type="button"
                  className="btn btn-outline-primary btn-sm px-3 py-1 shadow-sm"
                  onClick={() =>
                    fetchAvailableColors({ page: availablePage + 1 })
                  }
                  disabled={listLoading}
                  style={{ transition: "all 0.15s ease", fontSize: "0.8rem" }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.transform = "translateY(-1px)")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.transform = "translateY(0)")
                  }
                >
                  <i
                    className="mdi mdi-chevron-down"
                    style={{ fontSize: "0.9rem" }}
                  />
                  <span className="ms-1">Load More</span>
                </button>
              </div>
            )}
          </Modal.Body>
          <Modal.Footer className="border-top bg-light py-2 px-3">
            <button
              type="button"
              className="btn btn-light btn-sm px-3"
              onClick={closeAddModal}
              style={{ fontSize: "0.85rem" }}
            >
              <i className="mdi mdi-close" style={{ fontSize: "0.9rem" }} />
              <span className="ms-1">Cancel</span>
            </button>
            <button
              type="button"
              className="btn btn-primary btn-sm px-3 shadow-sm"
              onClick={handleAddSelectedColors}
              disabled={selectedAvailableIds.length === 0}
              style={{ transition: "all 0.15s ease", fontSize: "0.85rem" }}
              onMouseEnter={(e) =>
                !e.currentTarget.disabled &&
                (e.currentTarget.style.transform = "translateY(-1px)")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.transform = "translateY(0)")
              }
            >
              <i
                className="mdi mdi-plus-circle"
                style={{ fontSize: "0.9rem" }}
              />
              <span className="ms-1">Add ({selectedAvailableIds.length})</span>
            </button>
          </Modal.Footer>
        </Modal>
      )}
    </div>
  );
}
